import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Badge, Page, PageScroll, PageHeader, Tag } from '@indipia/ui-core';
import { photoApi, type SiteListItem } from '../api/photoApi';
import { usePhotoNavStore } from '../store/photoNavStore';
import { SearchBar, type SearchParams, type StatusFilter } from '../components/SearchBar';
import { ThumbImg } from '../components/ThumbImg';
import { highlight } from '../components/Highlight';
import { siteStatusBadge } from '../utils/siteStatus';
import { AssigneeBadges, useCompanyMembers, useDefaultMemberFilter, useMemberFilterState } from '@indipia/shared-features';
import { AssigneeFilterField } from '../components/AssigneeFilterField';
import type { AssignedMember } from '@indipia/shared-features';

const BASE = '/agents/photo-organizer';

interface SiteThumb {
  photoId: number;
  url: string | null;
}

interface Site {
  id: number;
  name: string;
  addr: string;
  customerName: string;
  status: string | null;
  siteStatus: string | null;
  siteStatusName: string | null;
  count: number;
  unclassified: number;
  lastUploadedAt: string | null;
  thumbColors: string[];
  thumbnails: SiteThumb[];
  chargeMembers: AssignedMember[];
  managerMembers: AssignedMember[];
}

const LIMIT = 20;
const UNC_THUMB_W = 72;
const UNC_THUMB_GAP = 10;
const UNC_ROW_PADDING_X = 20 * 2; // '14px 20px' — 좌우 padding 합

const THUMB_PALETTES: string[][] = [
  ['var(--gt-subtle)', 'var(--shell-skeleton)', 'var(--shell-bg)', 'var(--shell-surface)'],
  ['var(--shell-skeleton)', 'var(--gt-subtle)', 'var(--shell-surface)', 'var(--shell-bg)'],
  ['var(--shell-bg)', 'var(--shell-surface)', 'var(--gt-subtle)', 'var(--shell-skeleton)'],
  ['var(--shell-surface)', 'var(--shell-bg)', 'var(--shell-skeleton)', 'var(--gt-subtle)'],
];

const extractDong = (address: string): string => {
  const parts = address.replace(/[()]/g, '').split(/\s+/);
  const matches = parts.filter(p => /(?:동|읍|면|리)$/.test(p));
  return matches.length ? matches[matches.length - 1] : (parts[parts.length - 1] ?? address);
};

interface UnclassifiedItem {
  photoId: number;
  thumbUrl: string | null;
  takenDtm: string | null;
  gpsLat: number | null;
  gpsLng: number | null;
  sessionId: number | null;
  address?: string | null;
}

function relativeDate(dateStr: string): string {
  const diffDays = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (diffDays === 0) return '오늘';
  if (diffDays === 1) return '어제';
  if (diffDays < 7) return `${diffDays}일 전`;
  const weeks = Math.floor(diffDays / 7);
  if (weeks < 5) return `${weeks}주 전`;
  return `${Math.floor(diffDays / 30)}개월 전`;
}



export function SiteListMain() {
  const navigate = useNavigate();
  const clearUploadResult = usePhotoNavStore(s => s.clearUploadResult);
  const setClassifyShowAllSitesOnEntry = usePhotoNavStore(s => s.setClassifyShowAllSitesOnEntry);
  // 담당자·관리자 조건(2026-10-12 개선사항 D-4) — 기본값: 직원은 나, 사장님은 조건 없음(전체 현장)
  // 로그인 정보가 늦게 들어와도 올바른 기본값이 되도록 공용 훅으로 따로 둔다
  const { myId } = useDefaultMemberFilter();
  const { data: companyMembers = [] } = useCompanyMembers();
  const [memberIds, setMemberIds] = useMemberFilterState();
  const memberIdsKey = memberIds.join(',');
  const [searchParamsState, setSearchParamsState] = useState<{ keyword?: string; startDate?: string; endDate?: string }>({});
  // API 조회 조건 = 검색 조건 + 담당자·관리자 — 담당자 선택이 실제로 바뀔 때만 새 객체(재조회 트리거)
  const apiParams = useMemo(
    () => ({ ...searchParamsState, memberIds }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParamsState, memberIdsKey],
  );
  // 상단 요약(등록된 N개 현장)·상태 칩 건수 — 서버가 조건(담당자 포함)을 반영해 준 값(P-1③)
  const [serverCounts, setServerCounts] = useState<{ total: number; byStatus: Record<string, number> } | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [unclassified, setUnclassified] = useState<UnclassifiedItem[]>([]);
  const [unclassifiedTotal, setUnclassifiedTotal] = useState(0);
  const [uncMaxSlots, setUncMaxSlots] = useState(8);
  const [offset, setOffset] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const uncRowRef = useRef<HTMLDivElement>(null);
  const loadedUncCountRef = useRef(0);

  // 미분류 사진 행의 실제 폭에 맞춰 한 줄에 들어갈 썸네일 개수를 계산
  useEffect(() => {
    const el = uncRowRef.current;
    if (!el) return;
    const compute = () => {
      const contentWidth = el.clientWidth - UNC_ROW_PADDING_X;
      const cols = Math.floor((contentWidth + UNC_THUMB_GAP) / (UNC_THUMB_W + UNC_THUMB_GAP));
      setUncMaxSlots(Math.max(1, cols));
    };
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSearch = ({ keyword, startDate, endDate, statusFilter: sf }: SearchParams) => {
    setStatusFilter(sf ?? 'all');
    // statusFilter만 바뀐 경우 동일 참조를 반환해 useEffect 재호출 방지
    setSearchParamsState(prev => {
      if (prev.keyword === keyword && prev.startDate === startDate && prev.endDate === endDate) {
        return prev;
      }
      return { keyword, startDate, endDate };
    });
  };

  // 한 줄에 들어갈 개수(uncMaxSlots)만큼 미분류 사진을 불러온다 — 이미 그만큼 로드했다면 재호출하지 않음
  useEffect(() => {
    if (uncMaxSlots <= loadedUncCountRef.current) return;
    const controller = new AbortController();
    photoApi.getUnclassifiedPhotos(uncMaxSlots, 0, controller.signal)
      .then(res => {
        const ucData = res.data?.data;
        const items = ucData?.items ?? [];
        loadedUncCountRef.current = items.length;
        setUnclassifiedTotal(ucData?.totalCount ?? 0);
        setUnclassified(items);
      })
      .catch((err: { name?: string }) => { if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') throw err; });
    return () => controller.abort();
  }, [uncMaxSlots]);

  const mapSites = (raw: SiteListItem[], startIndex: number): Site[] =>
    raw.map(
      (s, i) => ({
        id: s.siteId,
        name: s.siteName,
        addr: s.address ?? '',
        customerName: s.customerName ?? '',
        status: s.siteStatus ?? null,
        siteStatus: s.siteStatus ?? null,
        siteStatusName: s.siteStatusName ?? null,
        count: s.photoCount,
        unclassified: s.unassignedCount,
        lastUploadedAt: s.lastUploadedAt ?? null,
        thumbColors: THUMB_PALETTES[(startIndex + i) % THUMB_PALETTES.length],
        thumbnails: (s.thumbnails ?? []).map(t => ({ photoId: t.photoId, url: t.url ?? null })),
        chargeMembers: s.chargeMembers ?? [],
        managerMembers: s.managerMembers ?? [],
      }),
    );

  useEffect(() => {
    setLoading(true);
    setFetchError(false);
    setSites([]);
    setOffset(0);
    setHasNext(false);
    const controller = new AbortController();

    photoApi.getSiteList({ limit: LIMIT, offset: 0, ...apiParams }, controller.signal)
      .then(res => {
        const sitesData = res.data?.data;
        const mapped = mapSites(sitesData?.sites ?? [], 0);
        // '전체'는 상태별 건수의 합 — totalSiteCount 는 상태 조건이 걸리면 그 상태 건수만 되므로 쓰지 않는다
        setServerCounts(sitesData?.statusCounts
          ? {
              total: Object.values(sitesData.statusCounts).reduce((a, n) => a + n, 0),
              byStatus: sitesData.statusCounts,
            }
          : null);
        setSites(mapped);
        setOffset(mapped.length);
        setHasNext(mapped.length < (sitesData?.totalSiteCount ?? 0));
      })
      .catch((err: { name?: string }) => { if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') setFetchError(true); })
      .finally(() => setLoading(false));

    return () => controller.abort();
  // mapSites is stable across renders (pure function of THUMB_PALETTES) — omitted from deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiParams]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasNext) return;
    setLoadingMore(true);
    photoApi.getSiteList({ limit: LIMIT, offset, ...apiParams })
      .then(res => {
        const sitesData = res.data?.data;
        const mapped = mapSites(sitesData?.sites ?? [], offset);
        setSites(prev => [...prev, ...mapped]);
        const newOffset = offset + mapped.length;
        setOffset(newOffset);
        setHasNext(newOffset < (sitesData?.totalSiteCount ?? 0));
      })
      .catch((err: { name?: string }) => { if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') console.error(err); })
      .finally(() => setLoadingMore(false));
  // mapSites intentionally omitted — pure function, stable across renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMore, hasNext, offset, apiParams]);

  useEffect(() => {
    const el = bottomRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const filtered = statusFilter === 'all'
    ? sites
    : sites.filter(s => s.status?.toUpperCase() === statusFilter);
  // 서버 건수가 있으면 그 값(조건 전체 기준), 없으면(구버전 BE) 지금까지 불러온 현장으로 센다
  const countOf = (status: string) => serverCounts
    ? (serverCounts.byStatus[status] ?? 0)
    : sites.filter(s => s.status === status).length;
  const totalSiteCount = serverCounts ? serverCounts.total : sites.length;
  const totalPhotos = sites.reduce((a, s) => a + s.count, 0);
  // 조회 조건(키워드·기간·상태·담당자)이 하나라도 걸려 있으면 빈 결과 문구를 '조건에 맞는 현장이 없습니다.'로
  const hasCondition = !!(searchParamsState.keyword || searchParamsState.startDate || searchParamsState.endDate)
    || statusFilter !== 'all' || memberIds.length > 0;
  // 오버플로우(+N) 타일이 필요하면 그만큼 슬롯을 양보
  const uncHasOverflow = unclassifiedTotal > uncMaxSlots;
  const uncVisibleCount = Math.min(unclassified.length, uncHasOverflow ? Math.max(0, uncMaxSlots - 1) : uncMaxSlots);


  return (
    // D-07 §21.4 fixed — 인디포토 공통 레이아웃이 높이를 묶으므로 doc 을 쓸 수 없다.
    // 스크롤·폭·패딩은 PageScroll 이 갖는다(§21.3 xl = 1440).
    <Page layout="fixed" style={{ background: 'var(--shell-bg)' }}>
      <PageScroll width="xl">

        <PageHeader
          title={
            <>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--gt-primary)', textTransform: 'uppercase' as const }}>
                <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M8 21v-6h8v6" /><path d="M3 9h18" />
                </svg>
                현장 관리
              </span>
              현장 목록
            </>
          }
          subtitle={`등록된 ${totalSiteCount}개 현장 · 등록 ${countOf('ACTIVE')}개 · 진행중 ${countOf('PROGRESS')}개 · 완료 ${countOf('DONE')}개`}
          extra={
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'var(--shell-surface)', border: '1px solid var(--shell-border)', borderRadius: 'var(--radius-lg)' }}>
                <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-sm)', background: 'var(--gt-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--type-heading-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface)', lineHeight: 1.2 }}>
                    {totalPhotos}<span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', marginLeft: 1 }}>장</span>
                  </div>
                  <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-on-surface-2)', marginTop: 1 }}>전체 사진</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'var(--shell-surface)', border: '1px solid color-mix(in srgb, var(--color-warning) 25%, transparent)', borderRadius: 'var(--radius-lg)' }}>
                <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-sm)', background: 'var(--shell-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--type-heading-sm-size)', fontWeight: 700, color: 'var(--color-warningStrong)', lineHeight: 1.2 }}>
                    {unclassifiedTotal}<span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 600, color: 'var(--color-warningStrong)', marginLeft: 1 }}>장</span>
                  </div>
                  <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--color-warningStrong)', marginTop: 1 }}>미분류 사진</div>
                </div>
              </div>
            </>
          }
        />

        {/* 섹션 묶음 — 흩어져 있던 22px·20px 여백을 gap 하나로 모은다 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-lg)' }}>

          {/* 검색 영역 */}
          <div style={{ background: 'var(--shell-surface)', border: '1px solid var(--shell-border)', borderRadius: 'var(--radius-xl)', padding: 'var(--gap-lg)' }}>
            <SearchBar
              onSearch={handleSearch}
              // 검색어 하나로 현장명 · 고객명 · 주소와 사진 태그(부분 일치)를 함께 찾는다 — 서버(GET /photo/sites)가 둘을 합친다
              placeholder="현장명 · 고객명 · 주소 · 사진 태그 검색"
              enableStatusFilter
              autoSearchOnMount
              statusCounts={{
                total:    totalSiteCount,
                ACTIVE:   countOf('ACTIVE'),
                PROGRESS: countOf('PROGRESS'),
                DONE:     countOf('DONE'),
              }}
              trailing={
                /* 담당자·관리자 조건 — 상태 칩 오른쪽, 라벨+선택 묶음(D-4) */
                <AssigneeFilterField
                  bordered
                  members={companyMembers}
                  values={memberIds}
                  onChange={setMemberIds}
                  myId={myId}
                />
              }
            />
          </div>

          {/* 미분류 사진 카드 */}
          <div style={{ border: '1.5px solid color-mix(in srgb, var(--gt-primary) 30%, transparent)', background: 'linear-gradient(160deg, var(--gt-subtle) 0%, var(--shell-surface) 100%)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
              <button onClick={() => { clearUploadResult(); setClassifyShowAllSitesOnEntry(true); navigate(`${BASE}/classify-site`); }} style={{ padding: '18px 20px 14px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', width: '100%', background: 'none', border: 'none', borderBottom: '1px solid var(--shell-border)', fontFamily: 'inherit', textAlign: 'left' }}>
                <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-lg)', background: 'color-mix(in srgb, var(--gt-primary) 12%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 'var(--type-heading-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface)' }}>미분류 사진</div>
                  <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-on-surface-2)', marginTop: 3 }}>현장에 배정되지 않은 사진입니다. 현장을 지정해 주세요.</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    <span style={{ fontSize: 'var(--type-title-md-size)', fontWeight: 700, color: 'var(--gt-primary)' }}>{unclassifiedTotal}<span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 600, color: 'color-mix(in srgb, var(--gt-primary) 70%, transparent)' }}>장</span></span>
                    <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)' }}>전체 미분류</span>
                  </div>
                  <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-sm)', background: 'color-mix(in srgb, var(--gt-primary) 12%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                  </div>
                </div>
              </button>
              <div ref={uncRowRef} style={{ padding: '14px 20px', display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' as const }}>
                {unclassified.slice(0, uncVisibleCount).map(p => {
                  const hasGps = p.gpsLat !== null;
                  const addressFailed = hasGps && !p.address;
                  return (
                    <div key={p.photoId} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, width: 72 }}>
                      <div style={{ width: 72, height: 72, borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1.5px solid color-mix(in srgb, var(--gt-primary) 25%, transparent)', background: 'var(--gt-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <ThumbImg url={p.thumbUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} fallback={
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
                          </svg>
                        } />
                      </div>
                      {!hasGps
                        ? <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)', textAlign: 'center' as const }}>GPS없음</span>
                        : addressFailed
                          ? <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--color-error)', fontWeight: 700, textAlign: 'center' as const }}>주소 변환 실패</span>
                          : <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--gt-primary)', fontWeight: 700, textAlign: 'center' as const }}>{p.address ? extractDong(p.address) : 'GPS'}</span>
                      }
                    </div>
                  );
                })}
                {uncHasOverflow && (
                  <div style={{ width: 72, height: 72, borderRadius: 'var(--radius-lg)', border: '1.5px solid color-mix(in srgb, var(--gt-primary) 25%, transparent)', background: 'var(--gt-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 'var(--type-heading-sm-size)', fontWeight: 700, color: 'var(--gt-primary)' }}>+{unclassifiedTotal - uncVisibleCount}</span>
                  </div>
                )}
                {unclassified.length === 0 && !loading && (
                  <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)' }}>미분류 사진이 없습니다.</span>
                )}
              </div>
            </div>

          {/* 현장 카드 그리드 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 16, alignItems: 'stretch', opacity: loading && sites.length > 0 ? 0.45 : 1, transition: 'opacity 0.25s ease', pointerEvents: loading ? 'none' : 'auto' }}>
            {fetchError && !loading
              ? (
                  <div style={{ gridColumn: '1 / -1', background: 'var(--shell-surface)', border: '1.5px solid var(--color-error)', borderRadius: 'var(--radius-xl)', padding: '48px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                    <div style={{ fontSize: 'var(--type-body-md-size)', fontWeight: 700, color: 'var(--color-error)' }}>현장 목록을 불러오지 못했습니다.</div>
                    <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)' }}>잠시 후 다시 시도해 주세요.</div>
                  </div>
                )
              : loading && sites.length === 0
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} style={{ background: 'var(--shell-skeleton)', borderRadius: 'var(--radius-xl)', height: 200 }} />
                ))
              : !loading && filtered.length === 0
              ? (
                  <div style={{ gridColumn: '1 / -1', background: 'var(--shell-surface)', border: '1.5px solid var(--shell-border)', borderRadius: 'var(--radius-xl)', padding: '48px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 52, height: 52, borderRadius: 'var(--radius-xl)', background: 'var(--shell-skeleton)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                      </svg>
                    </div>
                    <div style={{ fontSize: 'var(--type-body-md-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)' }}>{hasCondition ? '조건에 맞는 현장이 없습니다.' : '검색 결과가 없습니다.'}</div>
                    <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)' }}>키워드나 필터 조건을 변경해보세요.</div>
                  </div>
                )
              : filtered.map(site => (
                  // 카드 안에 담당자·관리자 배지(명단 팝오버 버튼)가 들어가므로 button 중첩을 피해 div role=button
                  <div
                    role="button"
                    tabIndex={0}
                    key={site.id}
                    onClick={() => navigate(`${BASE}/site/${site.id}`)}
                    onKeyDown={e => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); navigate(`${BASE}/site/${site.id}`); } }}
                    style={{ background: 'var(--shell-surface)', border: '1.5px solid var(--shell-border)', borderRadius: 'var(--radius-xl)', overflow: 'hidden', cursor: 'pointer', transition: 'all .2s', display: 'flex', flexDirection: 'column', width: '100%', padding: 0, textAlign: 'left', fontFamily: 'inherit' }}
                    onMouseOver={e => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = 'var(--gt-primary)'; el.style.boxShadow = '0 6px 22px color-mix(in srgb, var(--gt-primary) 14%, transparent)'; el.style.transform = 'translateY(-2px)'; }}
                    onMouseOut={e => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = 'var(--shell-border)'; el.style.boxShadow = 'none'; el.style.transform = 'none'; }}
                  >
                    {/* 썸네일 영역 */}
                    <div style={{ height: 120, position: 'relative', flexShrink: 0 }}>
                      {site.count === 0 ? (
                        <div style={{ position: 'absolute', inset: 0, background: 'var(--shell-skeleton)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="2" y1="2" x2="22" y2="22" />
                            <path d="M10.41 10.41a2 2 0 1 0 3.18 3.18" />
                            <path d="M13.5 6H5a2 2 0 0 0-2 2v10" />
                            <path d="M21 15.34V8a2 2 0 0 0-2-2h-.34" />
                            <path d="M21 21H3a2 2 0 0 1-2-2V8" />
                          </svg>
                          <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)', fontWeight: 500 }}>등록된 사진이 없습니다</span>
                        </div>
                      ) : (
                        <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: `repeat(${Math.min(site.thumbnails.length, 4)},1fr)`, gap: 2 }}>
                          {site.thumbnails.slice(0, 4).map((thumb, i) => (
                            <div
                              key={thumb.photoId}
                              style={{ background: site.thumbColors[i], display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
                            >
                              <ThumbImg url={thumb.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} fallback={
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
                                </svg>
                              } />
                            </div>
                          ))}
                        </div>
                      )}
                      {/* 상태 = Badge(D-09 §15.5) · 최근 업로드 = Tag(분류 표기라 뉴트럴) */}
                      <Badge
                        status={siteStatusBadge(site.status)}
                        style={{ position: 'absolute', top: 10, left: 12, pointerEvents: 'none' }}
                      >
                        {site.siteStatusName ?? site.siteStatus}
                      </Badge>
                      {site.lastUploadedAt && (
                        <Tag style={{ position: 'absolute', top: 10, right: 12, pointerEvents: 'none' }}>
                          {relativeDate(site.lastUploadedAt)}
                        </Tag>
                      )}
                      {site.count > 0 && (
                        <div style={{ position: 'absolute', bottom: 10, right: 12, display: 'flex', alignItems: 'center', gap: 5, pointerEvents: 'none' }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
                          <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--gray-0)' }}>{site.count}장</span>
                        </div>
                      )}
                    </div>

                    {/* 카드 본문 */}
                    <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <div style={{ fontSize: 'var(--type-body-md-size)', fontWeight: 700, color: 'var(--shell-on-surface)', marginBottom: 3, lineHeight: 1.35 }}>{highlight(site.name, apiParams.keyword ? [apiParams.keyword] : [])}</div>
                      {site.customerName && (
                        <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--gt-primary)', fontWeight: 600, marginBottom: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                          {highlight(site.customerName, apiParams.keyword ? [apiParams.keyword] : [])}
                        </div>
                      )}
                      <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-on-surface-2)', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                        {highlight(site.addr, apiParams.keyword ? [apiParams.keyword] : [])}
                        <div style={{ marginLeft: 'auto', width: 28, height: 28, borderRadius: 'var(--radius-sm)', background: 'var(--shell-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--shell-on-surface-2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                        </div>
                      </div>
                      {/* 담당자 · 관리자 (D-4) — 배지 클릭은 카드 클릭과 별개 */}
                      <AssigneeBadges
                        className="mt-03"
                        chargeMembers={site.chargeMembers}
                        managerMembers={site.managerMembers}
                        filterIds={memberIds}
                        myId={myId}
                      />
                    </div>
                  </div>
                ))
            }
          </div>

          <div ref={bottomRef} style={{ height: 1 }} />
          {loadingMore && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0', color: 'var(--shell-hint)', fontSize: 'var(--type-body-sm-size)' }}>
              불러오는 중...
            </div>
          )}
        </div>

      </PageScroll>
    </Page>
  );
}
